'use client';

import { useNavigate } from 'react-router-dom';

import { SettingsIcon, LogOutIcon, UserCircleIcon, SunMoonIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useUserProfile } from '@/states/user-profile';
import { EMPTY_VALUE } from '@/utils/strings';
import { getInitials } from '@/utils/auth';
import { useSidebar } from '@/hooks/use-sidebar-context';
import { useTheme } from '@/hooks/use-theme';
import { logout } from '@/libs/session-management';
import { ROUTES } from '@/router/routes';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

import { LanguageSwitcher } from '../language-switcher';

export function NavProfile() {
  const { t } = useTranslation();
  const { isMobile } = useSidebar();
  const { profile, clear } = useUserProfile();
  const { toggleTheme } = useTheme();
  const navigate = useNavigate();

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarFallback className="rounded-lg">{getInitials(profile)}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{profile?.full_name ?? EMPTY_VALUE}</span>
                <span className="truncate text-xs">{profile?.email}</span>
              </div>
              <SettingsIcon className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => navigate(ROUTES.PROFILE)}>
                <UserCircleIcon />
                {t('nav-profile')}
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <LanguageSwitcher isSideBar />
              <DropdownMenuItem onClick={toggleTheme}>
                <SunMoonIcon />
                Change Theme
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                onClick={() => {
                  clear();
                  logout();
                }}
              >
                <LogOutIcon />
                {t('nav-logout')}
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
